import React, { useState } from 'react';
import { View, StyleSheet, Text, ScrollView } from 'react-native';

import { useTranslation } from 'react-i18next';
import { CommonStyles, CommonProperties, Color } from '../../styles';
import SegmentedHeader from '../SegmentedControl/SegmentedHeader';
import BottomButton from '../BottomButton';
import AnimatedSwitch from '../AnimatedSwitch';
import { Counter } from '../TripSearchWidget/Counter';
import { i18nNS } from '../../i18n';
import useTextItemsClass from '../../utils/useTextItemsClass';
// Alert Import
import { useDispatch, useSelector } from 'react-redux';
import { openAlert } from '../../store/actions/alert';

import Icon from '../Icon';
import { AA_ENABLED, EVENTS_ENABLED } from '../../config/env';

const OptionCard = (props) => {
  const { text, underText, type, number, setNumber, disabled, minValue, maxValue } = props;
  const UnderText = ({ children }) => {
    return <Text style={styles.underText}>{`\n${children}`}</Text>;
  };
  return (
    <View style={styles.cardContainer}>
      <View style={styles.card}>
        <View style={styles.row}>
          <Icon size={28} name={type} style={CommonStyles.mr3} color={Color.text.low} />
          <Text style={styles.text}>
            {text} {underText !== undefined && <UnderText>{underText}</UnderText>}
          </Text>
          <View style={CommonStyles.flex1}>
            <View style={CommonStyles.alignSelfToEnd}>
              <Counter
                disabled={disabled}
                number={number}
                setNumber={setNumber}
                minValue={minValue}
                maxValue={maxValue}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const ChildrenTitleOptionCard = (props) => {
  return (
    <View style={styles.childrenCardContainer}>
      <View style={styles.card}>
        <Text style={styles.title}>{props.children}</Text>
      </View>
    </View>
  );
};

const ChildrenOptionCard = (props) => {
  const { number, setNumber, disabled, minValue, maxValue, setInSeat, inSeat } = props;

  return (
    <View style={styles.childrenCardContainer}>
      <View style={styles.card}>
        <View style={[styles.row, styles.spaceBetween]}>
          <Counter
            disabled={disabled}
            number={number}
            setNumber={setNumber}
            minValue={minValue}
            maxValue={maxValue}
          />
          <AnimatedSwitch
            onChangeType={(type) => {
              setInSeat(type);
            }}
            labelRight="In Seat"
            labelLeft="In Lap"
            disabled={number > 1 ? true : false}
            selectedOption={number > 1 ? 1 : inSeat ? 1 : 0}
          />
        </View>
      </View>
    </View>
  );
};

const SearchTripOptionsModal = (props) => {
  const { navigation, route } = props;
  const { returnScreen, currentAirClass, hasAir, hasHotel, hasCar, tripOptions, travelersLimit } =
    route.params || {};
  const { noOfCars, noOfRooms, persons } = tripOptions;
  const { noOfAdults, noOfSeniors, infants } = persons;
  const [selectedAirClass, setSelectedAirClass] = useState(currentAirClass);
  const { t } = useTranslation(i18nNS.searchWidget);
  //MinimumTravelers is set to enable seniors and children when allowed travelers is greater than this value in EVENTS app
  const minimumTravelers = 3;
  const maximumTravelers = EVENTS_ENABLED ? travelersLimit : undefined;
  const [items] = useTextItemsClass();
  const [cars, setCars] = useState(noOfCars);
  const [rooms, setRooms] = useState(noOfRooms);
  const [adults, setAdults] = useState(noOfAdults);
  const [seniors, setSeniors] = useState(noOfSeniors);
  const [children, setChildren] = useState(infants.length);
  const [infantsArray, setInfantsArray] = useState(infants);
  const dispatch = useDispatch();
  const user = useSelector((state) => state && state.auth && state.auth.user);
  const tripOptionsEnabled = (AA_ENABLED && user?.IsLeisureAllowed) || EVENTS_ENABLED;
  const childAndSeniorEnabled =
    (AA_ENABLED || travelersLimit >= minimumTravelers) && tripOptionsEnabled;

  const handleChildrenState = (newState) => {
    const newInfant = { age: 0, inSeat: false };
    setInfantsArray(
      newState < children
        ? infantsArray.slice(0, newState)
        : [...infantsArray, ...Array(newState - children).fill(newInfant)]
    );
    setChildren(newState);
  };

  const optionCards = [
    {
      text: 'Rooms',
      number: rooms,
      setNumber: setRooms,
      enabled: hasHotel,
      type: 'hotel',
      minValue: 1,
      maxValue: maximumTravelers,
    },
    {
      text: 'Cars',
      number: cars,
      setNumber: setCars,
      enabled: hasCar,
      type: 'car',
      minValue: 1,
      maxValue: maximumTravelers,
    },
    {
      text: 'Adults',
      underText: '(18+ Years Old)',
      number: adults,
      setNumber: setAdults,
      enabled: true,
      type: 'person',
      minValue: 1,
      maxValue: maximumTravelers,
    },
    {
      text: 'Seniors',
      underText: '(65+ Years Old)',
      number: seniors,
      setNumber: setSeniors,
      enabled: hasAir && childAndSeniorEnabled,
      type: 'person',
      minValue: 0,
      maxValue: maximumTravelers,
    },
    {
      text: 'Children',
      underText: '(Under 18 Years Old)',
      number: children,
      setNumber: handleChildrenState,
      enabled: hasAir && childAndSeniorEnabled,
      type: 'people',
      minValue: 0,
      style: 'children',
    },
  ];

  const setInSeat = (type, index) => {
    const infant = { age: infantsArray[index].age, inSeat: type === 1 ? true : false };
    setInfantsArray(
      infantsArray.map((inf, i) => {
        return i === index ? infant : inf;
      })
    );
  };

  const setAge = (age, index) => {
    const infant = { age, inSeat: infantsArray[index].inSeat };
    setInfantsArray(
      infantsArray.map((inf, i) => {
        return i === index ? infant : inf;
      })
    );
  };
  const noOfInfants = infantsArray.filter((item) => item.age < 2).length;

  const errorHandler = () => {
    const noOfPersons = adults + seniors;
    if (noOfPersons < 1) {
      dispatch(
        openAlert({
          title: 'You have no adults or seniors, so please adjust.',
        })
      );
      return true;
    }
    if (noOfPersons < cars) {
      dispatch(
        openAlert({
          title:
            'You have more cars than travelers, so please adjust. Just tell Uncle Bill to drive one car.',
        })
      );
      return true;
    }
    if (noOfPersons < rooms) {
      dispatch(
        openAlert({
          title:
            'You have more rooms than travelers, so please adjust. Just tell Uncle Bill to stay in one room',
        })
      );
      return true;
    }
    if (EVENTS_ENABLED && noOfPersons + children > travelersLimit) {
      dispatch(
        openAlert({
          title: `Sorry, we can only include a maximum of ${travelersLimit} travelers per trip at this time.`,
        })
      );
      return true;
    }
    if (noOfPersons < noOfInfants) {
      dispatch(
        openAlert({
          title:
            'Sorry, the number of infants can not be greater than the number of Adults/Seniors.',
        })
      );
      return true;
    }
    return false;
  };

  const submitHandler = () => {
    if (errorHandler()) {
      return;
    }
    const selectedTripOptions = {
      noOfCars: cars,
      noOfRooms: rooms,
      persons: {
        infants: infantsArray,
        noOfAdults: adults,
        noOfSeniors: seniors,
      },
    };
    navigation.navigate(returnScreen, {
      selectedAirClassType: selectedAirClass,
      selectedTripOptions,
    });
  };

  return (
    <>
      <ScrollView>
        <View style={CommonStyles.flex1}>
          <View style={styles.airClassContainer}>
            <SegmentedHeader
              style={CommonStyles.borderRadius}
              items={items}
              selected={selectedAirClass}
              onChange={setSelectedAirClass}
            />
          </View>

          {optionCards.map((option, index) => {
            const { text, underText, type, number, setNumber, minValue, maxValue, style } = option;
            return (
              option.enabled && (
                <OptionCard
                  key={index}
                  disabled={!tripOptionsEnabled}
                  text={text}
                  underText={underText}
                  type={type}
                  number={number}
                  setNumber={setNumber}
                  minValue={minValue}
                  maxValue={maxValue}
                  style={style}
                />
              )
            );
          })}

          {children > 0 && (
            <>
              <ChildrenTitleOptionCard>Age Of Children At Time Of Travel</ChildrenTitleOptionCard>
              {infantsArray.map((infant, index) => (
                <ChildrenOptionCard
                  key={index}
                  index={index}
                  disabled={!tripOptionsEnabled}
                  type="person"
                  number={infant.age}
                  inSeat={infant.inSeat}
                  setNumber={(age) => {
                    setAge(age, index);
                  }}
                  setInSeat={(type) => {
                    setInSeat(type, index);
                  }}
                  minValue={0}
                  maxValue={17}
                />
              ))}
            </>
          )}
        </View>
      </ScrollView>
      <View style={styles.bottomButtonContainer}>
        <BottomButton
          style={styles.bottomButton}
          label={t('common:Apply')}
          onPress={(e) => {
            // need to stop this from bubbling because it was activating the react navigation bottom bar and switching tabs
            e.preventDefault();
            submitHandler();
          }}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  airClassContainer: {
    ...CommonProperties.px3,
    ...CommonProperties.py2,
  },
  cardContainer: {
    ...CommonProperties.px3,
    ...CommonProperties.pt2,
  },
  childrenCardContainer: {
    ...CommonProperties.px3,
  },
  card: {
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
    ...CommonProperties.p3,
    borderColor: Color.text.extraLow,
    backgroundColor: Color.accent,
  },
  underText: {
    ...CommonProperties.h4,
    fontWeight: 'normal',
  },
  text: { ...CommonProperties.h3, fontWeight: 'bold' },
  title: { textAlign: 'center', fontWeight: 'bold' },
  spaceBetween: { justifyContent: 'space-between' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomButtonContainer: { flex: 1, justifyContent: 'flex-end' },
  bottomButton: { bottom: 0 },
});

export default SearchTripOptionsModal;
